import React, { useState, useEffect } from 'react';
import { Trash2, RotateCcw, AlertTriangle, Video, Film, CheckCircle, XCircle, History, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BulkVideoManagement = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('preview');
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [deletedItems, setDeletedItems] = useState([]);
  const [deletionLogs, setDeletionLogs] = useState([]);
  const [confirmationPhrase, setConfirmationPhrase] = useState('');
  const [hardDeletePhrase, setHardDeletePhrase] = useState('');
  
  const SOFT_DELETE_PHRASE = 'DELETE ALL VIDEOS';
  const HARD_DELETE_PHRASE = 'PERMANENTLY DELETE ALL VIDEOS';

  // Fetch preview data
  const fetchPreview = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/bulk/videos/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewData(response.data);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to fetch preview'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch soft-deleted items
  const fetchDeletedItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/bulk/videos/deleted`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeletedItems(response.data.items || []);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to fetch deleted items'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch deletion logs
  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/bulk/videos/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeletionLogs(response.data.logs || []);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to fetch logs'
      });
    } finally {
      setLoading(false);
    }
  };

  // Soft delete videos
  const handleSoftDelete = async () => {
    if (confirmationPhrase !== SOFT_DELETE_PHRASE) {
      toast({
        variant: 'destructive',
        title: 'Invalid Phrase',
        description: `You must type exactly: ${SOFT_DELETE_PHRASE}`
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/bulk/videos/soft-delete`,
        { confirmationPhrase: confirmationPhrase, reason: 'bulk video removal' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Success',
        description: response.data.message
      });
      
      setConfirmationPhrase('');
      fetchPreview();
      fetchDeletedItems();
      fetchLogs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to soft delete'
      });
    } finally {
      setLoading(false);
    }
  };

  // Restore items
  const handleRestore = async (batchId = null, contentIds = null) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/bulk/videos/restore`,
        { batchId, contentIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Success',
        description: response.data.message
      });
      
      fetchPreview();
      fetchDeletedItems();
      fetchLogs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to restore'
      });
    } finally {
      setLoading(false);
    }
  };

  // Hard delete (permanent)
  const handleHardDelete = async () => {
    if (hardDeletePhrase !== HARD_DELETE_PHRASE) {
      toast({
        variant: 'destructive',
        title: 'Invalid Phrase',
        description: `You must type exactly: ${HARD_DELETE_PHRASE}`
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/bulk/videos/hard-delete`,
        { confirmationPhrase: hardDeletePhrase },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Permanently Deleted',
        description: response.data.message
      });
      
      setHardDeletePhrase('');
      fetchDeletedItems();
      fetchLogs();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to permanently delete'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreview();
  }, []);

  useEffect(() => {
    if (activeTab === 'deleted') fetchDeletedItems();
    if (activeTab === 'logs') fetchLogs();
  }, [activeTab]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Video className="w-6 h-6 text-red-500" />
            Bulk Video Management
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Remove or manage movies and videos in bulk
          </p>
        </div>
        <Button
          onClick={() => {
            fetchPreview();
            if (activeTab === 'deleted') fetchDeletedItems();
            if (activeTab === 'logs') fetchLogs();
          }}
          variant="outline"
          className="border-gray-600"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-800">
          <TabsTrigger value="preview" className="data-[state=active]:bg-red-500">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Videos
          </TabsTrigger>
          <TabsTrigger value="deleted" className="data-[state=active]:bg-yellow-500">
            <RotateCcw className="w-4 h-4 mr-2" />
            Restore
          </TabsTrigger>
          <TabsTrigger value="logs" className="data-[state=active]:bg-blue-500">
            <History className="w-4 h-4 mr-2" />
            Logs
          </TabsTrigger>
        </TabsList>

        {/* PREVIEW & DELETE TAB */}
        <TabsContent value="preview" className="mt-4">
          <Card className="bg-gray-900 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Preview Video Deletion
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {previewData ? (
                <>
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-800 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-red-400">{previewData.totalCount}</div>
                      <div className="text-gray-400 text-sm">Total Items</div>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-purple-400 flex items-center justify-center gap-2">
                        <Film className="w-6 h-6" />
                        {previewData.moviesCount}
                      </div>
                      <div className="text-gray-400 text-sm">Movies</div>
                    </div>
                    <div className="bg-gray-800 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-blue-400 flex items-center justify-center gap-2">
                        <Video className="w-6 h-6" />
                        {previewData.videosCount}
                      </div>
                      <div className="text-gray-400 text-sm">Videos</div>
                    </div>
                  </div>

                  {/* Confirmation Message */}
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <p className="text-yellow-400 font-medium text-center">
                      {previewData.confirmationMessage}
                    </p>
                  </div>

                  {/* Soft Delete Form */}
                  {previewData.totalCount > 0 && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 space-y-4">
                      <div className="flex items-center gap-2 text-red-400">
                        <AlertTriangle className="w-5 h-5" />
                        <span className="font-semibold">Soft Delete (Recoverable)</span>
                      </div>
                      <p className="text-gray-300 text-sm">
                        This will hide all movies and videos from the UI. Files will NOT be deleted.
                        You can restore them later.
                      </p>
                      <div className="space-y-2">
                        <label className="text-gray-400 text-sm">
                          Type <span className="text-red-400 font-mono">{SOFT_DELETE_PHRASE}</span> to confirm:
                        </label>
                        <Input
                          value={confirmationPhrase}
                          onChange={(e) => setConfirmationPhrase(e.target.value)}
                          placeholder="Type confirmation phrase..."
                          className="bg-gray-800 border-gray-600 text-white font-mono"
                        />
                      </div>
                      <Button
                        onClick={handleSoftDelete}
                        disabled={loading || confirmationPhrase !== SOFT_DELETE_PHRASE}
                        className="w-full bg-red-600 hover:bg-red-700 text-white"
                      >
                        {loading ? 'Processing...' : 'Soft Delete All Videos & Movies'}
                      </Button>
                    </div>
                  )}

                  {previewData.totalCount === 0 && (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                      <p className="text-gray-400">No movies or videos to delete</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  {loading ? 'Loading preview...' : 'Failed to load preview'}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* RESTORE TAB */}
        <TabsContent value="deleted" className="mt-4">
          <Card className="bg-gray-900 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-yellow-500" />
                Soft-Deleted Items ({deletedItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {deletedItems.length > 0 ? (
                <>
                  {/* Restore All Button */}
                  <div className="flex justify-end gap-2">
                    <Button
                      onClick={() => handleRestore(null, deletedItems.map(i => i.id))}
                      disabled={loading}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Restore All
                    </Button>
                  </div>

                  {/* Items List */}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {deletedItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between bg-gray-800 rounded-lg p-3"
                      >
                        <div className="flex items-center gap-3">
                          {item.mediaType === 'movie' ? (
                            <Film className="w-5 h-5 text-purple-400" />
                          ) : (
                            <Video className="w-5 h-5 text-blue-400" />
                          )}
                          <div>
                            <p className="text-white font-medium">{item.title}</p>
                            <p className="text-gray-400 text-xs">
                              Deleted: {new Date(item.deletedAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="border-gray-600">
                            {item.mediaType}
                          </Badge>
                          <Button
                            size="sm"
                            onClick={() => handleRestore(null, [item.id])}
                            disabled={loading}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Restore
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Hard Delete Section */}
                  <div className="mt-6 bg-red-900/20 border border-red-500/30 rounded-lg p-4 space-y-4">
                    <div className="flex items-center gap-2 text-red-400">
                      <XCircle className="w-5 h-5" />
                      <span className="font-semibold">Permanent Deletion (IRREVERSIBLE)</span>
                    </div>
                    <p className="text-gray-300 text-sm">
                      This will permanently delete all soft-deleted videos/movies and their files.
                      This action cannot be undone.
                    </p>
                    <div className="space-y-2">
                      <label className="text-gray-400 text-sm">
                        Type <span className="text-red-400 font-mono">{HARD_DELETE_PHRASE}</span> to confirm:
                      </label>
                      <Input
                        value={hardDeletePhrase}
                        onChange={(e) => setHardDeletePhrase(e.target.value)}
                        placeholder="Type confirmation phrase..."
                        className="bg-gray-800 border-red-900 text-white font-mono"
                      />
                    </div>
                    <Button
                      onClick={handleHardDelete}
                      disabled={loading || hardDeletePhrase !== HARD_DELETE_PHRASE}
                      className="w-full bg-red-800 hover:bg-red-900 text-white"
                    >
                      {loading ? 'Processing...' : 'Permanently Delete All'}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-400">No soft-deleted items</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* LOGS TAB */}
        <TabsContent value="logs" className="mt-4">
          <Card className="bg-gray-900 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <History className="w-5 h-5 text-blue-500" />
                Deletion Logs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {deletionLogs.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {deletionLogs.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-center justify-between bg-gray-800 rounded-lg p-3"
                    >
                      <div className="flex items-center gap-3">
                        {log.actionType === 'SOFT_DELETE' && (
                          <Trash2 className="w-5 h-5 text-yellow-400" />
                        )}
                        {log.actionType === 'RESTORE' && (
                          <RotateCcw className="w-5 h-5 text-green-400" />
                        )}
                        {log.actionType === 'HARD_DELETE' && (
                          <XCircle className="w-5 h-5 text-red-400" />
                        )}
                        <div>
                          <p className="text-white font-medium">
                            {log.contentTitle || log.contentId}
                          </p>
                          <p className="text-gray-400 text-xs">
                            By {log.adminUsername} • {new Date(log.timestampUtc).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={
                            log.actionType === 'SOFT_DELETE'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : log.actionType === 'RESTORE'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }
                        >
                          {log.actionType}
                        </Badge>
                        <Badge variant="outline" className="border-gray-600">
                          {log.mediaType}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <History className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No deletion logs yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BulkVideoManagement;
